import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Users,
  User,
  UserRound,
  TrendingUp,
  TrendingDown,
  Briefcase,
  RefreshCw,
  Download,
  FileText,
  GitCompare,
  Building2,
  AlertTriangle,
} from "lucide-react";
import toast from "react-hot-toast";
import {
  buildEmployeeCardDetail,
  computeEmployeeCompareSeries,
  computeEmployeeDashboardAlerts,
  computeEmployeeDashboardMetrics,
  computeEmployeeMasterBuckets,
  computeEmployeeMonthlyMovement,
  computeTenureBuckets,
} from "../../lib/employeeDashboardMetrics";
import {
  buildYearOptionsFromMin,
  clampYearMonthIndex,
  isMonthIndexBeforeMin,
  resolveCompanyMinDate,
} from "../../utils/selectableDateBounds";
import {
  DsBadge,
  DsButton,
  DsCard,
  DsIcon,
  DsInput,
  DsLabel,
  DsPageHeader,
} from "../../design-system";
import {
  AnalyticsChartPanel,
  CHART_HEX,
  DashCombo,
  DashDonut,
  DashHBar,
} from "./payroll/payrollDashboardCharts";
import {
  DashboardCardDetailModal,
  DashboardCompareModal,
  DashboardLoadingOverlay,
  DashboardMetricCard,
  recentPayMonths,
} from "./dashboardSharedUi";
import { exportEmployeeDashboardExcel } from "./exportWorkforceDashboardExcel";
import {
  exportEmployeeDashboardPdf,
  exportWorkforceCardPdf,
} from "./exportWorkforceDashboardPdf";

function MiniSpark({ values = [], color = CHART_HEX[0] }) {
  const nums = (values || []).map((v) => Number(v) || 0);
  if (nums.length < 2) return null;
  const max = Math.max(...nums, 1);
  const w = 72;
  const h = 22;
  const step = w / (nums.length - 1);
  const points = nums
    .map((v, i) => `${i * step},${h - (v / max) * (h - 2) - 1}`)
    .join(" ");
  return (
    <svg width={w} height={h} className="shrink-0" aria-hidden>
      <polyline fill="none" stroke={color} strokeWidth="1.75" points={points} />
    </svg>
  );
}

function AlertsPanel({ alerts = [] }) {
  if (!alerts.length) {
    return (
      <div className="rounded-lg border border-[hsl(var(--ds-border))] px-3 py-6 text-center text-xs text-[hsl(var(--ds-muted-fg))]">
        No workforce alerts for this view.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {alerts.map((a) => (
        <div
          key={a.id}
          className={`rounded-lg border px-3 py-2 text-xs ${
            a.tone === "warning"
              ? "border-[hsl(var(--ds-warning)/0.4)] bg-[hsl(var(--ds-warning)/0.12)]"
              : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)]"
          }`}
        >
          <div className="flex items-start gap-2">
            <DsIcon icon={AlertTriangle} size="sm" className="mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold text-[hsl(var(--ds-fg))]">{a.title}</div>
              <div className="mt-0.5 text-[hsl(var(--ds-muted-fg))]">{a.detail}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const COMPARE_ASPECTS = [
  { id: "active", label: "Active HC" },
  { id: "joins", label: "Joins" },
  { id: "exits", label: "Exits" },
  { id: "gender", label: "Gender" },
  { id: "probation", label: "Probation" },
  { id: "masters", label: "Master-wise" },
];

/**
 * Workforce Dashboard — Payroll Dashboard framework (period, pulse, charts, tabs, Excel).
 * Data: existing computeEmployeeDashboardMetrics + client-side masters/alerts/trends only.
 */
export default function EmployeeDashboardPanel({
  darkMode = false,
  isFlipped = false,
  onFlip,
  showFlipButton = true,
  employees = [],
  loading = false,
  canExport = true,
}) {
  const now = new Date();
  const companyMinDate = useMemo(() => resolveCompanyMinDate(), []);
  const [periodMode, setPeriodMode] = useState("month");
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [rangeFrom, setRangeFrom] = useState("");
  const [rangeTo, setRangeTo] = useState("");
  const [groupBy, setGroupBy] = useState("department");
  const [dashTab, setDashTab] = useState("overview");
  const [exporting, setExporting] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const [compareMonths, setCompareMonths] = useState([]);
  const [compareAspects, setCompareAspects] = useState(() =>
    COMPARE_ASPECTS.map((a) => a.id)
  );
  const [compareOpen, setCompareOpen] = useState(false);
  const [cardDetail, setCardDetail] = useState(null);

  const setPeriodYear = (nextYear) => {
    const period = clampYearMonthIndex(nextYear, month - 1, companyMinDate);
    setYear(period.year);
    setMonth(period.month + 1);
  };
  const setPeriodMonth = (nextMonth1to12) => {
    const period = clampYearMonthIndex(year, nextMonth1to12 - 1, companyMinDate);
    setYear(period.year);
    setMonth(period.month + 1);
  };

  useEffect(() => {
    if (periodMode !== "range") return;
    if (rangeFrom && rangeTo) return;
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
    if (!rangeFrom) setRangeFrom(`01-${m}-${y}`);
    if (!rangeTo) setRangeTo(`${lastDay}-${m}-${y}`);
  }, [periodMode, rangeFrom, rangeTo, now]);

  const metrics = useMemo(
    () =>
      computeEmployeeDashboardMetrics(employees, {
        periodMode,
        month,
        year,
        rangeFrom,
        rangeTo,
      }),
    [employees, periodMode, month, year, rangeFrom, rangeTo, refreshTick]
  );

  const masters = useMemo(
    () => computeEmployeeMasterBuckets(employees, groupBy),
    [employees, groupBy, refreshTick]
  );

  const alerts = useMemo(
    () => computeEmployeeDashboardAlerts(employees),
    [employees, refreshTick]
  );

  const movement = useMemo(
    () => computeEmployeeMonthlyMovement(employees, year),
    [employees, year, refreshTick]
  );

  const tenure = useMemo(
    () => computeTenureBuckets(employees),
    [employees, refreshTick]
  );

  const compareSeries = useMemo(() => {
    if (compareMonths.length < 2) return [];
    return computeEmployeeCompareSeries(employees, compareMonths);
  }, [employees, compareMonths, refreshTick]);

  const availableCompareMonths = useMemo(
    () => recentPayMonths(18).map((ym) => ({ payMonth: ym })),
    []
  );

  const yearOptions = useMemo(
    () =>
      buildYearOptionsFromMin({
        minDate: companyMinDate,
        currentYear: now.getFullYear(),
        yearsBefore: 7,
        yearsAfter: 0,
      }).reverse(),
    [companyMinDate, now]
  );

  const fieldClass =
    "h-8 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] px-2 text-xs text-[hsl(var(--ds-fg))] outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))]";

  const showDashboardBody = isFlipped || showFlipButton === false;

  const genderChart = useMemo(
    () =>
      [
        { label: "Men", value: metrics.male },
        { label: "Women", value: metrics.female },
        { label: "Other / unknown", value: metrics.otherGender },
      ].filter((d) => d.value > 0),
    [metrics]
  );

  const statusChart = useMemo(
    () =>
      [
        { label: "Probation", value: metrics.probation },
        { label: "Confirmed", value: metrics.confirmed },
        { label: "Relieved", value: metrics.relieved },
        { label: "Resigned", value: metrics.resigned },
        { label: "Terminated", value: metrics.terminated },
      ].filter((d) => d.value > 0),
    [metrics]
  );

  const TAB_ITEMS = [
    { id: "overview", label: "Overview" },
    { id: "alerts", label: `Alerts${alerts.length ? ` (${alerts.length})` : ""}` },
    { id: "structure", label: "Structure" },
    { id: "movements", label: "Movements" },
    { id: "mix", label: "Mix" },
  ];

  const openCardDetail = useCallback(
    (cardKey) => {
      setCardDetail(buildEmployeeCardDetail(cardKey, { metrics, tenure }));
    },
    [metrics, tenure]
  );

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      await exportEmployeeDashboardExcel({
        metrics,
        masters,
        alerts,
        movement,
        tenure,
        compare: compareSeries,
        periodLabel: metrics.periodLabel,
        groupBy,
      });
      toast.success("Workforce dashboard exported");
    } catch (err) {
      toast.error(err?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExporting(true);
    try {
      const name = exportEmployeeDashboardPdf({
        metrics,
        masters,
        alerts,
        movement,
        tenure,
        compare: compareSeries,
        periodLabel: metrics.periodLabel,
      });
      toast.success(`Exported ${name}`);
    } catch (err) {
      toast.error(err?.message || "PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  const renderCompareSection = () => {
    if (compareMonths.length < 2) return null;

    const showCombo =
      compareAspects.includes("active") ||
      compareAspects.includes("joins") ||
      compareAspects.includes("exits");

    return (
      <div className="mt-4">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="ds-small uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
            Comparison
          </div>
          <div className="text-[10px] text-[hsl(var(--ds-muted-fg))]">
            Aspects:{" "}
            {COMPARE_ASPECTS.filter((a) => compareAspects.includes(a.id))
              .map((a) => a.label)
              .join(", ")}
          </div>
        </div>
        <DsCard padding="sm" className="!p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="ds-table w-full text-xs">
              <thead>
                <tr>
                  <th className="px-3 py-2 text-left">Month</th>
                  {compareAspects.includes("active") ? (
                    <th className="px-3 py-2 text-right">Active</th>
                  ) : null}
                  {compareAspects.includes("joins") ? (
                    <th className="px-3 py-2 text-right">Joins</th>
                  ) : null}
                  {compareAspects.includes("exits") ? (
                    <th className="px-3 py-2 text-right">Exits</th>
                  ) : null}
                  {compareAspects.includes("gender") ? (
                    <>
                      <th className="px-3 py-2 text-right">Men</th>
                      <th className="px-3 py-2 text-right">Women</th>
                    </>
                  ) : null}
                  {compareAspects.includes("probation") ? (
                    <th className="px-3 py-2 text-right">Probation</th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {compareSeries.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="px-3 py-6 text-center text-[hsl(var(--ds-muted-fg))]"
                    >
                      No comparison data for selected months.
                    </td>
                  </tr>
                ) : (
                  compareSeries.map((row) => (
                    <tr key={row.payMonth} className="border-t border-[hsl(var(--ds-border))]">
                      <td className="px-3 py-1.5 font-medium">{row.label}</td>
                      {compareAspects.includes("active") ? (
                        <td className="px-3 py-1.5 text-right tabular-nums">{row.active}</td>
                      ) : null}
                      {compareAspects.includes("joins") ? (
                        <td className="px-3 py-1.5 text-right tabular-nums">{row.joins}</td>
                      ) : null}
                      {compareAspects.includes("exits") ? (
                        <td className="px-3 py-1.5 text-right tabular-nums">{row.exits}</td>
                      ) : null}
                      {compareAspects.includes("gender") ? (
                        <>
                          <td className="px-3 py-1.5 text-right tabular-nums">{row.male}</td>
                          <td className="px-3 py-1.5 text-right tabular-nums">{row.female}</td>
                        </>
                      ) : null}
                      {compareAspects.includes("probation") ? (
                        <td className="px-3 py-1.5 text-right tabular-nums">{row.probation}</td>
                      ) : null}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {showCombo && compareSeries.length > 0 ? (
            <div className="border-t border-[hsl(var(--ds-border))] px-3 py-3">
              <DashCombo
                labels={compareSeries.map((r) => r.label)}
                barValues={compareSeries.map((r) => r.joins)}
                lineValues={compareSeries.map((r) => r.exits)}
                barLabel="Joins"
                lineLabel="Exits"
              />
              <p className="mt-2 text-[10px] text-[hsl(var(--ds-muted-fg))]">
                Active headcount by month-end is in the table above.
              </p>
            </div>
          ) : null}
          {compareAspects.includes("masters") ? (
            <div className="border-t border-[hsl(var(--ds-border))] px-3 py-3 text-[11px] text-[hsl(var(--ds-muted-fg))]">
              Master-wise breakdown for compare months is on the Structure tab. Excel/PDF include
              the full comparison summary.
            </div>
          ) : null}
        </DsCard>
      </div>
    );
  };

  const renderTenureCard = (onClick = null) => (
    <DsCard padding="sm" className="!p-3">
      <div className="ds-small mb-2 uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
        Tenure mix (active)
      </div>
      <button
        type="button"
        className={`w-full text-left ${onClick ? "cursor-pointer rounded-md outline-none transition hover:ring-2 hover:ring-[hsl(var(--ds-ring))] focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))]" : ""}`}
        onClick={onClick || undefined}
        disabled={!onClick}
      >
        <DashDonut data={tenure} />
        {onClick ? (
          <div className="mt-1 text-[9px] text-[hsl(var(--ds-primary))]">Click for details</div>
        ) : null}
      </button>
    </DsCard>
  );

  return (
    <>
      {showFlipButton && (
        <DsButton type="button" variant="outline" size="sm" onClick={onFlip}>
          <DsIcon icon={RefreshCw} size="sm" />
          {isFlipped ? "Back" : "Flip Dashboard"}
        </DsButton>
      )}

      {showDashboardBody && (
        <div
          className={`relative flex min-h-0 flex-1 flex-col overflow-y-auto bg-[hsl(var(--ds-surface))] ${
            showFlipButton ? "rounded-xl border border-[hsl(var(--ds-border))]" : "h-full"
          }`}
        >
          <DashboardLoadingOverlay show={loading} label="Loading workforce analytics…" />
          <div className="ds-page-panel-head">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <DsPageHeader
                title="Workforce Dashboard"
                description={`Period: ${metrics.periodLabel}${loading ? " · Updating…" : ""}`}
              />
              <div className="flex flex-wrap items-center gap-2">
                <DsBadge tone="neutral">Live roster</DsBadge>
                <DsButton
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setCompareOpen(true)}
                >
                  <DsIcon icon={GitCompare} size="sm" />
                  Compare
                </DsButton>
                {canExport ? (
                  <>
                    <DsButton
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={exporting || loading}
                      onClick={handleExportExcel}
                    >
                      <DsIcon icon={Download} size="sm" />
                      Excel
                    </DsButton>
                    <DsButton
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={exporting || loading}
                      onClick={handleExportPdf}
                    >
                      <DsIcon icon={FileText} size="sm" />
                      PDF
                    </DsButton>
                  </>
                ) : null}
                <DsButton
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={loading}
                  onClick={() => setRefreshTick((t) => t + 1)}
                >
                  <DsIcon
                    icon={RefreshCw}
                    size="sm"
                    className={loading ? "animate-spin" : ""}
                  />
                  Refresh
                </DsButton>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4 lg:px-6">
            <div className="mb-4 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)] p-3">
              <div className="ds-small mb-2 uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                Period &amp; grouping
              </div>
              <div className="flex flex-wrap items-end gap-2">
                {[
                  { id: "month", label: "Month" },
                  { id: "year", label: "Year" },
                  { id: "range", label: "Period" },
                ].map((tab) => (
                  <DsButton
                    key={tab.id}
                    type="button"
                    size="sm"
                    variant={periodMode === tab.id ? "secondary" : "ghost"}
                    onClick={() => setPeriodMode(tab.id)}
                  >
                    {tab.label}
                  </DsButton>
                ))}

                {periodMode === "month" && (
                  <>
                    <select
                      className={fieldClass}
                      value={month}
                      onChange={(e) => setPeriodMonth(Number(e.target.value))}
                      aria-label="Month"
                    >
                      {Array.from({ length: 12 }, (_, i) => (
                        <option
                          key={i + 1}
                          value={i + 1}
                          disabled={isMonthIndexBeforeMin(year, i, companyMinDate)}
                        >
                          {new Date(2000, i, 1).toLocaleString("default", {
                            month: "long",
                          })}
                        </option>
                      ))}
                    </select>
                    <select
                      className={fieldClass}
                      value={year}
                      onChange={(e) => setPeriodYear(Number(e.target.value))}
                      aria-label="Year"
                    >
                      {yearOptions.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                  </>
                )}

                {periodMode === "year" && (
                  <select
                    className={fieldClass}
                    value={year}
                    onChange={(e) => setPeriodYear(Number(e.target.value))}
                    aria-label="Year"
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                )}

                {periodMode === "range" && (
                  <>
                    <div>
                      <DsLabel htmlFor="emp-dash-range-from">From (DD-MM-YYYY)</DsLabel>
                      <DsInput
                        id="emp-dash-range-from"
                        type="text"
                        className="h-8 max-w-[9rem] text-xs"
                        value={rangeFrom}
                        onChange={(e) => setRangeFrom(e.target.value)}
                        placeholder="01-01-2026"
                      />
                    </div>
                    <div>
                      <DsLabel htmlFor="emp-dash-range-to">To (DD-MM-YYYY)</DsLabel>
                      <DsInput
                        id="emp-dash-range-to"
                        type="text"
                        className="h-8 max-w-[9rem] text-xs"
                        value={rangeTo}
                        onChange={(e) => setRangeTo(e.target.value)}
                        placeholder="31-03-2026"
                      />
                    </div>
                  </>
                )}

                <div>
                  <DsLabel htmlFor="emp-dash-groupby">Master</DsLabel>
                  <select
                    id="emp-dash-groupby"
                    className={fieldClass}
                    value={groupBy}
                    onChange={(e) => setGroupBy(e.target.value)}
                  >
                    <option value="department">Department</option>
                    <option value="branch">Branch</option>
                    <option value="designation">Designation</option>
                  </select>
                </div>
              </div>
              {compareMonths.length > 0 ? (
                <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-[hsl(var(--ds-primary)/0.35)] bg-[hsl(var(--ds-primary)/0.08)] px-2 py-1.5 text-[11px]">
                  <span className="font-semibold text-[hsl(var(--ds-fg))]">
                    Comparing: {compareMonths.join(", ")}
                  </span>
                  <button
                    type="button"
                    className="text-[hsl(var(--ds-primary))] underline"
                    onClick={() => setCompareMonths([])}
                  >
                    Clear
                  </button>
                </div>
              ) : null}
            </div>

            <div className="mb-4 flex flex-wrap gap-1 border-b border-[hsl(var(--ds-border))] pb-2">
              {TAB_ITEMS.map((tab) => (
                <DsButton
                  key={tab.id}
                  type="button"
                  size="sm"
                  variant={dashTab === tab.id ? "secondary" : "ghost"}
                  onClick={() => setDashTab(tab.id)}
                >
                  {tab.label}
                </DsButton>
              ))}
            </div>

            {dashTab === "alerts" ? (
              <AlertsPanel alerts={alerts} />
            ) : dashTab === "structure" ? (
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <DsCard padding="sm" className="!p-3">
                  <div className="ds-small mb-2 uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                    Active HC by {groupBy}
                  </div>
                  <DashHBar data={masters} valueFormat="count" maxBars={12} />
                </DsCard>
                <DsCard padding="sm" className="!p-3">
                  <DashDonut data={masters.slice(0, 8)} />
                </DsCard>
              </div>
            ) : dashTab === "movements" ? (
              <DsCard padding="sm" className="!p-3">
                <AnalyticsChartPanel
                  title={`Joins vs exits — ${movement.year}`}
                  labels={movement.labels}
                  values={movement.joins}
                  lineValues={movement.exits}
                  defaultMode="combo"
                />
                <p className="mt-2 text-[10px] text-[hsl(var(--ds-muted-fg))]">
                  Bars = joins · Line = exits (from DOJ / exit dates on roster).
                </p>
              </DsCard>
            ) : dashTab === "mix" ? (
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
                <DsCard padding="sm" className="!p-3">
                  <div className="ds-small mb-2 uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                    Gender mix
                  </div>
                  <DashDonut data={genderChart} />
                </DsCard>
                <DsCard padding="sm" className="!p-3">
                  <div className="ds-small mb-2 uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                    Employment status
                  </div>
                  <DashHBar data={statusChart} valueFormat="count" />
                </DsCard>
                {renderTenureCard(() => openCardDetail("tenure"))}
              </div>
            ) : (
              <>
                {alerts.length > 0 ? (
                  <div className="mb-3">
                    <AlertsPanel alerts={alerts.slice(0, 3)} />
                    {alerts.length > 3 ? (
                      <button
                        type="button"
                        className="mt-1 text-[10px] text-[hsl(var(--ds-primary))] underline"
                        onClick={() => setDashTab("alerts")}
                      >
                        View all {alerts.length} alerts
                      </button>
                    ) : null}
                  </div>
                ) : null}

                <div className="mb-2 ds-small uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                  Company pulse
                </div>
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
                  <DashboardMetricCard
                    label="Active HC"
                    value={metrics.active}
                    sub={`${metrics.headcount} in view`}
                    icon={Users}
                    spark={<MiniSpark values={movement.joins} color={CHART_HEX[5]} />}
                    onClick={() => openCardDetail("headcount")}
                  />
                  <DashboardMetricCard
                    label="New joinees"
                    value={metrics.newJoinees}
                    sub={`${metrics.newJoineeRate}% vs active`}
                    icon={TrendingUp}
                    tone="up"
                    spark={<MiniSpark values={movement.joins} color={CHART_HEX[2]} />}
                    onClick={() => openCardDetail("joins")}
                  />
                  <DashboardMetricCard
                    label="Attrition"
                    value={metrics.attrition}
                    sub={`${metrics.attritionRate}% vs active`}
                    icon={TrendingDown}
                    tone="down"
                    spark={<MiniSpark values={movement.exits} color={CHART_HEX[4]} />}
                    onClick={() => openCardDetail("attrition")}
                  />
                  <DashboardMetricCard
                    label="Probation"
                    value={metrics.probation}
                    sub={`Confirmed ${metrics.confirmed}`}
                    icon={Briefcase}
                    onClick={() => openCardDetail("headcount")}
                  />
                  <DashboardMetricCard
                    label="Men"
                    value={metrics.male}
                    sub={
                      metrics.male + metrics.female > 0
                        ? `${metrics.malePct}% of known gender`
                        : "No gender data"
                    }
                    icon={User}
                    onClick={() => openCardDetail("gender")}
                  />
                  <DashboardMetricCard
                    label="Women"
                    value={metrics.female}
                    sub={
                      metrics.male + metrics.female > 0
                        ? `${metrics.femalePct}% · M:F ${metrics.genderRatio}`
                        : "No gender data"
                    }
                    icon={UserRound}
                    onClick={() => openCardDetail("gender")}
                  />
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
                  <DsCard padding="sm" className="!p-3">
                    <AnalyticsChartPanel
                      title={`Joins vs exits — ${movement.year}`}
                      labels={movement.labels}
                      values={movement.joins}
                      lineValues={movement.exits}
                      defaultMode="combo"
                    />
                  </DsCard>
                  <DsCard padding="sm" className="!p-3">
                    <div className="ds-small mb-2 uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                      Active HC by {groupBy}
                    </div>
                    <DashHBar data={masters} valueFormat="count" maxBars={8} />
                  </DsCard>
                  {renderTenureCard(() => openCardDetail("tenure"))}
                </div>

                {renderCompareSection()}

                <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <DashboardMetricCard label="Full-time" value={metrics.fullTime} icon={Building2} />
                  <DashboardMetricCard label="External" value={metrics.external} />
                  <DashboardMetricCard
                    label="Relieved"
                    value={metrics.relieved}
                    tone="down"
                    onClick={() => openCardDetail("attrition")}
                  />
                  <DashboardMetricCard
                    label="Resigned"
                    value={metrics.resigned}
                    tone="down"
                    onClick={() => openCardDetail("attrition")}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <DashboardCompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        availableMonths={availableCompareMonths}
        selected={compareMonths}
        aspects={COMPARE_ASPECTS}
        selectedAspects={compareAspects}
        title="Compare workforce months"
        onApply={({ months, aspects }) => {
          setCompareMonths(months || []);
          setCompareAspects(
            aspects?.length ? aspects : COMPARE_ASPECTS.map((a) => a.id)
          );
        }}
      />
      <DashboardCardDetailModal
        open={!!cardDetail}
        detail={cardDetail}
        onClose={() => setCardDetail(null)}
        canExport={canExport}
        onExportExcel={(detail) => exportEmployeeDashboardExcel({ cardDetail: detail })}
        onExportPdf={exportWorkforceCardPdf}
      />
    </>
  );
}
